import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpenCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function RulesAcceptanceModal() {
  const { user, refresh } = useAuth();
  const [rules, setRules] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/rules");
        if (cancelled) return;
        if (data.needs_acceptance) {
          setRules(data);
          setOpen(true);
          // Fire-and-forget analytics ping
          api.post("/rules/shown").catch(() => {});
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user]);

  const accept = async () => {
    if (!agreed) return;
    setSaving(true);
    try {
      await api.post("/auth/accept-rules");
      toast.success("Thanks for joining the community!");
      setOpen(false);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!rules) return null;

  const isReprompt = (rules.user_accepted_version || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={() => { /* forbid closing without accepting */ }}>
      <DialogContent
        className="bg-[#FFFBF5] border-[#EBDCD1] rounded-2xl max-w-lg max-h-[85vh] flex flex-col [&>button.absolute]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl sm:text-3xl text-[#3B2C24] flex items-center gap-2">
            <BookOpenCheck className="w-6 h-6 text-[#FF7E67]" />
            {isReprompt ? "Our House Rules were updated" : "Welcome — please review our House Rules"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#857163]">
            {isReprompt
              ? "We've updated the community guidelines. Please re-read and agree to keep enjoying Cake Pop Talk."
              : "Before joining the conversation, please take a moment to read our community guidelines."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 my-2 border border-[#EBDCD1] rounded-2xl p-4 bg-white" data-testid="rules-modal-content">
          {rules.content.split(/\n\n+/).map((block, i) => {
            const trimmed = block.trim();
            if (!trimmed) return null;
            const lines = trimmed.split("\n");
            const first = lines[0];
            const rest = lines.slice(1).join("\n").trim();
            const isRule = /^\d+\.\s+/.test(first);
            return (
              <div key={i} className="mb-4 last:mb-0">
                {isRule ? (
                  <>
                    <h4 className="font-heading text-base text-[#3B2C24] leading-snug">
                      <span className="text-[#FF7E67] font-bold mr-1">
                        {first.match(/^(\d+)\./)[1]}.
                      </span>
                      {first.replace(/^\d+\.\s+/, "")}
                    </h4>
                    {rest && <p className="mt-1 text-xs text-[#3B2C24]/80 whitespace-pre-wrap">{rest}</p>}
                  </>
                ) : (
                  <p className="text-sm text-[#3B2C24] whitespace-pre-wrap">{trimmed}</p>
                )}
              </div>
            );
          })}
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="rules-modal-agree-label">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 accent-[#FF7E67]"
            data-testid="rules-modal-checkbox"
          />
          <span className="text-sm text-[#3B2C24]">
            I've read and agree to follow these House Rules. I understand that violating them may result in
            removed posts or a ban. <Link to="/rules" target="_blank" className="text-[#FF7E67] hover:underline">Open full rules</Link>
          </span>
        </label>

        <button
          onClick={accept}
          disabled={!agreed || saving}
          className="cp-btn-primary w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="rules-modal-accept-btn"
        >
          {saving ? "Saving…" : (isReprompt ? "Accept updated rules" : "I agree — let me in")}
        </button>
      </DialogContent>
    </Dialog>
  );
}
